import { CheckCircle2, Target, Eye, Compass } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import CTASection from "@/components/CTASection";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const About = () => {
  useDocumentTitle("About");
  return (
    <div data-testid="about-page">
      <PageHeader
        eyebrow="About Akyvia"
        title="Engineering excellence, born from scientific curiosity."
        description="Akyvia Sciences and Technologies is a research-driven technology company advancing the frontiers of engineering, intelligent systems and applied science."
        breadcrumb={[{ label: "Home", to: "/" }, { label: "About" }]}
      />

      {/* Intro */}
      <section className="py-20 lg:py-24 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <h2 className="font-display text-3xl lg:text-4xl font-semibold text-[#0B1B3D] tracking-tight leading-tight">
              A company built on innovation, research and engineering excellence.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-slate-600 leading-relaxed">
            <p>
              We combine deep scientific research with disciplined engineering to
              design, manufacture and deploy advanced technology products — from
              special-purpose electric motors and intelligent motion control to
              autonomous aviation systems, industrial AI, and space instrumentation.
            </p>
            <p>
              Our teams work across applied physics, control theory, embedded
              systems, machine learning, mechatronics and aerospace — enabling
              universities, industries, and mission programs to move faster with
              precision and confidence.
            </p>
          </div>
        </div>
      </section>

      {/* Vision / Mission / Values */}
      <section className="py-20 lg:py-24 bg-gradient-to-br from-white to-sky-50/40 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-6">
          {[
            {
              Icon: Eye,
              t: "Vision",
              d: "To be a world-class research and engineering organization advancing science, technology and human progress.",
            },
            {
              Icon: Target,
              t: "Mission",
              d: "Design and deliver research-grade engineering products, systems and training that create measurable impact.",
            },
            {
              Icon: Compass,
              t: "Values",
              d: "Scientific integrity, engineering rigor, precision and long-term collaboration.",
            },
          ].map(({ Icon, t, d }) => (
            <div
              key={t}
              className="bg-white border border-slate-200 rounded-xl p-8 hover:border-sky-300 transition-colors"
            >
              <div className="w-11 h-11 rounded-md bg-[#0B1B3D]/5 border border-slate-200 flex items-center justify-center mb-5">
                <Icon className="w-5 h-5 text-[#0B1B3D]" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-xl font-semibold text-[#0B1B3D]">{t}</h3>
              <p className="mt-3 text-slate-600 text-sm leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why Akyvia */}
      <section className="py-20 lg:py-24 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
            <h2 className="font-display text-3xl lg:text-4xl font-semibold text-[#0B1B3D] tracking-tight">
              Why organizations choose Akyvia.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              "Multi-disciplinary research and engineering teams",
              "In-house manufacturing of motors, controllers and electronics",
              "Rigorous testing, validation and traceability",
              "Deep experience in AI, aerospace and industrial systems",
              "Strong academic and industrial partnerships",
              "End-to-end lifecycle: research → prototype → production",
            ].map((t) => (
              <div
                key={t}
                className="flex items-start gap-3 border border-slate-200 rounded-md p-5 bg-white"
              >
                <CheckCircle2
                  className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0"
                  strokeWidth={1.5}
                />
                <span className="text-sm text-slate-700 leading-relaxed">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Partner with Akyvia"
        title="Let's engineer what's next, together."
        description="Whether you're an industry leader, research institute or academic partner — we'd love to hear from you."
      />
      <div className="py-24" />
    </div>
  );
};

export default About;
